import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Dimmer, Form, Grid, Loader, Modal, Segment, Confirm } from 'semantic-ui-react'
import { useTextResources, useMessageResources } from '../../language'
import PropTypes from 'prop-types'
import TemplatesDropDown from '../../components/shared/TemplatesDropdown'
import { onError, onSuccess, onWarning } from '../../components/shared/messages'
import { CalculateAmountTax, GetTransactionCodes, GetCurrencyRates, GetInvoiceSettlementBalance, RenderTemplateWithReCalcTax, SendEmail, SettleAndPostInvoice, ValidateInvoiceSettlement } from '../../api/services/finance'
import { convertStatic } from 'react-html-converter/browser'
import moment from 'moment'
import { useLoader } from '../../components/shared/utils'
import saveAs from 'file-saver'
import InvoiceDetailsPopup from './invoceDetailsPopup'
import { RenderTemplateToPdf } from '../../api/services/pms'
import CurrencyDropDown from '../../components/shared/CurrencyDropDown'
import _ from 'lodash'
import { EntityType, HotelFeatureCodes } from '../../components/shared/constants'
import { GetCustomers, GetGuests } from '../../api/services/crm'
import PaymentTabs from '../../components/Payment/paymentTabs'
import { PermissionsActions, UserHasPermission } from '../../components/shared/permissionsActions'
import DropDown from '../../components/shared//dropdown';
import { useLocalStorageState } from '../../hooks/useLocalStorageState';
import { Invoice } from '../../api/services/next'
import { maxCustomerNameLength } from '../../components/shared/constants'

const ProformaPopup = ({ onCancel, hotel: initHotel, folio: initFolio, transactionIds: initTransactionIds, controller, open: startOpen, currencyId, ...rest }) => {
    const text = useTextResources()
    const message = useMessageResources()
    const [open, setOpen] = useState(startOpen)
    const [hotel, setHotel] = useState(initHotel)
    const [folio, setFolio] = useState(initFolio)
    const [transactionIds, setTransactionIds] = useState(initTransactionIds || [])
    const [transactions, setTransactions] = useState([])
    // HS-69601: balance of the selected transactions as the invoice will calculate it (tax exemptions included).
    // null while loading or if the call failed; the client-side sum is used until then.
    const [serverBalance, setServerBalance] = useState(null)
    const [serverBalanceLoading, setServerBalanceLoading] = useState(false)
    const [serverBalanceError, setServerBalanceError] = useState(false)
    const [currencyRates, setCurrencyRates] = useState([])
    const [params, setParams] = useState(null)
    const loader = useLoader()
    const [activeTab, setActiveTab] = useState(0)
    const [guestDetails, setGuestDetails] = useState({})
    const [customerDetails, setCustomerDetails] = useState({})
    const [roundingTransactionCode, setRoundingTransctionCode] = useState(0)
    const [state, setState] = useState({
        selectedPaymentCurrency: currencyId || 1,
        paymentCurrency: currencyId || 1,
        displayName: "",
        amount: 0,
        hotelCurrency: hotel && hotel.MainCurrencyId || 1,
        ignoreSelectedValueForPaymentCurrency: true
    })
    const dialogRef = useRef()
    const [templateId, setTemplateId] = useState()
    const [template, setTemplate] = useState({})
    const [refund, setRefund] = useState(false)
    const [templateItem, setTemplateItem] = useState({})
    const refundAction = 151
    const [groupedBy, setGroupedBy] = useLocalStorageState("InvoiceGroupBy")
    const hasRefundAuthorization =  window?.optima?.user?.ActionIDs?.includes(refundAction) || false



    const hasNegativeBalance = useMemo(() => {
        return state.amount < 0
    }, [state.amount])
    const [confirmationNumber, setConfirmationNumber] = useState('false')
    const [confirmationNumberDisabled, setConfirmationNumberDisabled] = useState('false')
    const [confirmPopupStatus, setConfirmPopupStatus] = useState(false);
    const [displaySave, setDisplaySave] = useState(true);

    const groupedBySelection = [
        { key: 'TransactionCodeGroups', text: text('TransactionCodeGroups') },
        { key: 'TransactionCodeDates', text: text('TransactionCodeByDates') },
        { key: 'Product', text: text('Products') }
    ]


    const calculateCurrencyValue = (value, currencyID, targetCurrencyID) => {
        const rate = currencyRates.find(x => x.SourceCurrencyId === currencyID && x.TargetCurrencyId === targetCurrencyID)
        if (rate) {
            return value * rate.PaymentRate
        } else {
            return value
        }
    }

    const updateOwnerDetails = async owner => {
        if (owner) {
            try {
                const guests = await GetGuests({
                    IDs: owner.EntityId,
                    IncludeLocation: true
                })
                if (guests && guests.length) {
                    const guest = guests[0]
                    setGuestDetails({
                        fname: guest.FirstName,
                        lname: guest.LastName,
                        email: guest.Email,
                        phone: guest.Phone,
                        idnumber: guest.IdentityCardNumber,
                        countryId: guest?.Location?.CountryID,
                        stateId: guest?.Location?.StateID,
                        street: guest?.Location?.StreetAndHomeNumber,
                        city: guest?.Location?.City,
                        zip: guest?.Location?.Zip,
                    })
                } else {
                    setGuestDetails({})
                }
            } catch (error) {
                onError(error)
            }
            try {
                const customers = await GetCustomers({
                    IDs: owner.EntityId
                })
                if (customers && customers.Customer.length) {
                    const customer = customers.Customer[0]
                    setCustomerDetails({
                        vatNumber: customer.VatNumber,
                        name: customer.Name
                    })
                } else {
                    setCustomerDetails({})
                }
            } catch (error) {
                onError(error)
            }
        }
    }


    useEffect(() => {
        setHotel(initHotel)
        setFolio(initFolio)
        setTransactionIds(initTransactionIds || [])
        if (initFolio && initFolio.Owner) {
            updateOwnerDetails(initFolio.Owner)
        } else {
            setGuestDetails({})
        }
        setOpen(startOpen)
    }, [initHotel, initFolio, initTransactionIds, startOpen])

    useEffect(() => {
        if (hotel) {
            (async () => {
                try {
                    const currencyRates = await GetCurrencyRates({
                        HotelID: hotel.Id,
                        Date: moment(hotel.HotelParameter.CurrentBusinessDate).startOf("day").toISOString(),
                    })
                    setCurrencyRates(Array.isArray(currencyRates) ? currencyRates : [])
                    const transactionCodes = await GetTransactionCodes({
                        HotelId: hotel.Id,
                        TransactionCodeHardcodedTypeID: 7 // Rounding
                    })
                    if (transactionCodes && transactionCodes.length) {
                        setRoundingTransctionCode(transactionCodes[0])
                    }

                } catch (error) {
                    onError(error)
                }
            })()
        }
    }, [hotel])

    useEffect(() => {
        setState(prev => ({
            ...prev,
            hotelCurrency: hotel && hotel.MainCurrencyId || 1,
            sendEmail: hotel.HotelParameter.IsDefaultSendInvoiceByMail
        }))
    }, [hotel])

    const invoiceReservationId = () => {
        const resEntity = folio && folio.FolioEntities.find(x => x.EntityTypeId === 1)
        return resEntity && resEntity.EntityId
    }

    useEffect(() => {
        let cancelled = false
        setServerBalance(null)
        setServerBalanceError(false)
        const shouldFetch = !!(folio && hotel && transactionIds && transactionIds.length)
        // also clears a loading flag left by a cancelled request
        setServerBalanceLoading(shouldFetch)
        if (shouldFetch) {
            (async () => {
                try {
                    const balance = await GetInvoiceSettlementBalance({
                        CurrencyID: state.selectedPaymentCurrency,
                        HotelId: hotel.Id,
                        ReservationID: invoiceReservationId(),
                        FolioTransactions: folio.FolioTransactions.filter(x => transactionIds.includes(x.Id) && !x.IsTax)
                    })
                    if (!cancelled) {
                        setServerBalance(balance)
                    }
                } catch (error) {
                    // HS-69601: without the server balance the grid sum may include an exempted tax - block issuing
                    if (!cancelled) {
                        setServerBalanceError(true)
                        error.status === 400 ? onWarning(error) : onError(error)
                    }
                } finally {
                    if (!cancelled) {
                        setServerBalanceLoading(false)
                    }
                }
            })()
        }
        return () => { cancelled = true }
    }, [folio, hotel, transactionIds, state.selectedPaymentCurrency])

    useEffect(() => {
        (async () => {
            if (folio && transactionIds) {
                const transactions = folio.FolioTransactions.filter(
                    x => transactionIds.includes(x.Id) || // Include selected transactions
                        transactionIds.includes(x.ParentTaxFolioTransactionTaxDetailID) // Include related tax transactions
                )
                const clientAmount = transactions.reduce((prev, transaction) => {
                    return prev + calculateCurrencyValue(transaction.AmountBeforeTax * transaction.Quantity, transaction.CurrencyId, state.selectedPaymentCurrency)
                }, 0)
                let amount = serverBalance !== null ? serverBalance : clientAmount

                const params = hotel.HotelParameter
                if (!refund &&
                    params.RoundToPrecision &&
                    amount % params.RoundToPrecision > 0.005) {
                    // We should insert a rounding transaction here
                    const paymentAmount = amount
                    let roundingAmount = paymentAmount % params.RoundToPrecision
                    roundingAmount = (params.IsAllowRoundingUp ? params.RoundToPrecision : 0) - roundingAmount
                    roundingAmount = calculateCurrencyValue(roundingAmount, state.selectedPaymentCurrency, state.hotelCurrency)
                    const roundingTransaction = {
                        Id: 0,
                        AmountAfterTax: roundingAmount,
                        Comments: "Rounding transaction",
                        CurrencyId: state.hotelCurrency,
                        FolioId: folio && folio.Id,
                        HotelID: hotel && hotel.Id,
                        IsVoid: false,
                        IsTax: false,
                        IsCalculateAfterTax: false,
                        TransactionDisplayName: "Rounding transaction",
                        TransactionCodeId: roundingTransactionCode.Id,
                        TransactionDate: moment().toISOString(),
                        PrintDate: moment().toISOString(),
                        TransactionCode: {
                            TransactionCodeGroupID: roundingTransactionCode.TransactionCodeGroupID,
                            IsDebit: true
                        },
                        Quantity: 1
                    }
                    const result = await CalculateAmountTax({
                        HotelId: hotel && hotel.Id,
                        IsProductPricingIncludeTax: false,
                        Transactions: [roundingTransaction]
                    })
                    transactions.push({
                        ...roundingTransaction,
                        AmountBeforeTax: result[0].AmountBeforeTax,
                        IsRounding: true
                    })
                }
                setTransactions(transactions)
            } else {
                setTransactions([])
            }
        })()
    }, [folio, transactionIds, state.selectedPaymentCurrency, refund, serverBalance])

    useEffect(() => {
        let amount = 0
        amount = transactions.reduce((prev, transaction) => {
            // HS-69601: with the server balance only the rounding transaction is added on the client
            if (serverBalance !== null && !transaction.IsRounding) {
                return prev
            }
            return prev + calculateCurrencyValue(
                transaction.IsRounding ? transaction.AmountAfterTax : transaction.AmountBeforeTax * transaction.Quantity,
                transaction.CurrencyId,
                state.selectedPaymentCurrency)
        }, serverBalance !== null ? serverBalance : 0)

        amount = Math.round(amount * 100) / 100

        setState(prev => ({
            ...prev,
            HardcodedTypeIDs: folio?.Owner?.EntityTypeId === EntityType.Guest ? [6] : null,
            amount: amount,
        }))
        setRefund(amount < 0)
    }, [transactions, state.selectedPaymentCurrency, serverBalance])

    useEffect(() => {
        if (controller) {
            window[controller] = (action, params) => {
                switch (action) {
                    case "open":
                        if (params) {
                            setActiveTab(0)
                            setHotel(params.hotel)
                            setFolio(params.folio)
                            setState(prev => ({
                                ...prev,
                                selectedPaymentCurrency: params.currencyId || state.selectedPaymentCurrency,
                                paymentCurrency: params.currencyId || state.paymentCurrency,
                                displayName: "",
                                transactionCodeID: null,
                                isAgentOrCompany: params.isAgentOrCompany,
                                isCityLadger: params.isCityLadger
                            }))
                            setTransactionIds(params.transactionIds)
                            if (params.folio && params.folio.Owner) {
                                updateOwnerDetails(params.folio.Owner)
                            } else {
                                setGuestDetails({})
                            }
                            setParams(params)
                        }
                        setOpen(true)
                        break;
                    default:
                        break;
                }
            }
        }
    }, [])

    useEffect(() => {
        const minPrice = window?.globalSettings?.TaxAuthorityMinPrice

        const transactions = folio?.FolioTransactions?.filter(
            x => transactionIds.includes(x.Id) || // Include selected transactions
                transactionIds.includes(x.ParentTaxFolioTransactionTaxDetailID) // Include related tax transactions
        )

        const totalDebitPrice = transactions?.filter(x => x.TransactionCode?.IsDebit && !x.IsTax && x.AmountBeforeTax != x.AmountAfterTax)
            .reduce((total, ft) => {
                return total + ft.AmountBeforeTax * ft.Quantity;
            }, 0)

        const totalTaxes = _.sumBy(transactions?.filter(x => x.IsTax), "AmountBeforeTax")

        const selfInvoice = transactions?.filter(x => x.OriginalInvoiceIdAllocationRejected).length > 0

        const confirmationNumberRequired = (folio?.Owner?.EntityTypeId == EntityType.COMPANY ||
            folio?.Owner?.EntityTypeId == EntityType.AGENT) &&
            totalDebitPrice >= minPrice

        if (!minPrice || (totalTaxes == 0 && !selfInvoice)) {
            setConfirmationNumber(false)
            setConfirmationNumberDisabled(true)
        }
        else {
            if (selfInvoice || confirmationNumberRequired) {
                setConfirmationNumber(true)
                setConfirmationNumberDisabled(true)
            }
            else {
                setConfirmationNumber(false)
                setConfirmationNumberDisabled(false)
            }
        }
        if (open && hasNegativeBalance && !hasRefundAuthorization) {
            onWarning(message("RefundNotPermittedForLoggedInUser"))
        }
    }, [open])

    const templatesSpec = useMemo(() => {
        return {
            HotelID: hotel && hotel.Id,
            TemplateTypeID: 10,
            IncludeTemplateBody: false,
            IncludeLanguage: true,
            isSystemTemplate: true
        }
    }, [hotel])

    const parameters = (title, folioTransactions, recalcTaxes) => {
        const resEntity = folio && folio.FolioEntities.find(x => x.EntityTypeId === 1)
        const result = [
            { key: "CurrencyId", value: state.paymentCurrency },
            { key: "FolioId", value: folio && folio.Id || null },
            { key: "FolioTransactionIds", value: folioTransactions ? folioTransactions.map(x => x.Id).join(",") : transactionIds.join(",") },
            { key: "ReCalcTaxExemptions", value: recalcTaxes === undefined ? true : recalcTaxes },
            { key: "CalculateAdditionalTaxes", value: true },
            { key: "IsGroupByProduct", value: hotel.HotelParameter.IsDisplayFolioTransactionsGroupedByProduct },
            { key: "IsGroupByTransactionCodeAndType", value: true },
            { key: "IssuedByUserName", value: window.user ? `${window.user.FirstName} ${window.user.LastName}` : "Developer" },
            { key: "ReservationId", value: resEntity && resEntity.EntityId || null },
            { key: "PaymentCurrencyId", value: state.selectedPaymentCurrency },
            { key: "InvoiceTitle", value: title || "Statement" },
            { key: "GuestTitle", value: guestDetails.salutation || "" },
            { key: "GuestFirstName", value: guestDetails.fname || folio.Owner?.Name || "" },
            { key: "GuestLastName", value: guestDetails.lname || "" },
            { key: "CustomerVATNumber", value: guestDetails.vat || "" },
            { key: "CustomerName", value: guestDetails.customerName || "" },
            { key: "GuestStreetAndNumber", value: guestDetails.street || "" },
            { key: "GuestCity", value: guestDetails.city || "" },
            { key: "GuestZip", value: guestDetails.zip || "" },
            { key: "GuestCountryName", value: guestDetails.country || "" },
            { key: "GuestState", value: guestDetails.state || "" },
            { key: "GuestPhone", value: guestDetails.phone || "" },
            { key: "GuestEmail", value: guestDetails.email || "" },
            { key: "GuestIdentityCardNumber", value: guestDetails.idnumber || "" },
            { key: "Comments", value: guestDetails.comments || "" }
        ]
        const roundingTransaction = transactions.find(x => x.TransactionCodeId === roundingTransactionCode.Id)
        if (roundingTransaction) {
            let transaction = {
                ...roundingTransaction,
                AmountBeforeTaxByTargetCurrencyID: calculateCurrencyValue(roundingTransaction.AmountBeforeTax, state.hotelCurrency, state.selectedPaymentCurrency),
                AmountAfterTaxByTargetCurrencyID: calculateCurrencyValue(roundingTransaction.AmountAfterTax, state.hotelCurrency, state.selectedPaymentCurrency)
            }
            result.push({
                key: "FolioTransactions", value: JSON.stringify([transaction])
            })
        }
        return result
    }

    useEffect(() => {
        if (templateId) {
            _.throttle(async () => {
                try {
                    setTemplate({})
                    const template = await RenderTemplateWithReCalcTax({
                        HotelID: hotel && hotel.Id,
                        TemplateID: templateId,
                        Paramters: parameters(),
                        GroupBy: groupedBy?.toString()
                    })
                    setTemplate(template)
                } catch (error) {
                    onError(error)
                }
            }, 100)()
        }
    }, [groupedBy, templateId, state.selectedPaymentCurrency, state.paymentCurrency, state.hotelCurrency, transactionIds, transactions, guestDetails])

    const print = async () => {
        try {
            const template = await RenderTemplateWithReCalcTax({
                HotelID: hotel && hotel.Id,
                TemplateID: templateId,
                IsPrintable: true,
                FileName: "statement",
                Paramters: parameters(),
                GroupBy: groupedBy?.toString()
            })
            if (template.PdfDto) {
                saveAs(template.PdfDto.DmzUrlPathAccessibleByBrowser)
            }
        } catch (error) {
            error.status === 400 ? onWarning(error) : onError(error)
        }
    }

    const send = async () => {
        try {
            const template = await RenderTemplateWithReCalcTax({
                HotelID: hotel && hotel.Id,
                TemplateID: templateId,
                IsPrintable: true,
                FileName: "statement",
                Paramters: parameters(),
                GroupBy: groupedBy?.toString()
            })
            if (template.PdfDto) {
                template.PdfDto.DmzUrlPathAccessibleByBrowser = null
                template.IsAttachBodyAsPdf = true
                template.To = guestDetails.email
                template.Subject = text("Statement")
                await SendEmail(template)
                onSuccess("SentSuccessfully")
            }
        } catch (error) {
            error.status === 400 ? onWarning(error) : onError(error)
        }
    }

    const save = async () => {
        try {
            loader(true)
            //Check self invoice creation
            var selfInvoice = transactions.find(x =>  x.OriginalInvoiceIdAllocationRejected)

            const resEntity = folio && folio.FolioEntities.find(x => x.EntityTypeId === 1)
            const _amount = state.amount * -1;
            let slip = null
            if (activeTab === 1 && dialogRef.current) {
                // HS-69601: the card is charged before SettleAndPostInvoice, so check the invoice balance first -
                // a failed invoice cannot undo the charge
                await ValidateInvoiceSettlement({
                    CurrencyID: state.paymentCurrency,
                    HotelId: hotel && hotel.Id,
                    ReservationID: resEntity && resEntity.EntityId,
                    FolioTransactions: transactions.filter(x => !x.IsTax && x.Id !== 0),
                    NewFolioTransactions: [
                        ...transactions.filter(x => x.Id === 0 && !x.IsTax),
                        ...(isZeroBalance() ? [] : [{
                            Id: 0,
                            Amount: _amount,
                            AmountBeforeTax: _amount,
                            AmountAfterTax: _amount,
                            CurrencyId: state.selectedPaymentCurrency,
                            FolioId: folio && folio.Id,
                            HotelID: hotel && hotel.Id,
                            Quantity: 1
                        }])
                    ]
                })
                slip = await dialogRef.current.saveAction();
                loader(true)
            }
            // Insert balancing debit transaction
            let newtransactions = []
            let checksDetails = []
            if (state.ChecksDetails) {
                checksDetails.push({
                    ID: state.ChecksDetails.Id,
                    BankID: state.ChecksDetails.BankID,
                    BranchCode: state.ChecksDetails.BranchCode,
                    CheckNumber: state.ChecksDetails.CheckNumber,
                    CheckDate: state.ChecksDetails.CheckDate,
                    AccountNumber: state.ChecksDetails.AccountNumber,
                    FolioTransactionID: state.ChecksDetails.FolioTransactionID
                })
            }
            newtransactions.push(...transactions.filter(x => x.Id === 0 && !x.IsTax))

            if (!isZeroBalance() && !slip) {
                newtransactions.push({
                    Id: 0,
                    Amount: _amount,
                    AmountBeforeTax: _amount,
                    AmountAfterTax: _amount,
                    Comments: state.comments,
                    CurrencyId: state.selectedPaymentCurrency,
                    FolioId: folio && folio.Id,
                    HotelID: hotel && hotel.Id,
                    IsVoid: false,
                    TransactionDisplayName: state.displayName,
                    TransactionCodeId: state.transactionCodeID,
                    Voucher: state.voucher,
                    Quantity: 1,
                    SendEmailTo: state.sendEmail && guestDetails.email,
                    ChecksDetails: checksDetails,
                    OriginalInvoiceIdAllocationRejected: selfInvoice ? selfInvoice.OriginalInvoiceIdAllocationRejected : null,
                    IsTaxExempt: selfInvoice ? selfInvoice.IsTaxExempt : null
                })

            }

            // HS-69601: the payment is saved by SettleAndPostInvoice together with the invoice
            const unsavedTransactions = newtransactions
            newtransactions = []

            if (slip) {
                newtransactions.push({
                    Id: slip.FolioTransactionID,
                    TransactionCodeId: slip.TransactionCodeId,
                    FolioId: folio && folio.Id,
                    HotelID: hotel && hotel.Id,
                    Amount: _amount,
                    AmountBeforeTax: _amount,
                    AmountAfterTax: _amount,
                    AmountByTargetCurrencyID: _amount,
                    CurrencyId: state.selectedPaymentCurrency,
                    Quantity: 1,
                    IsDebit: false,
                    IsVoid: false
                })
            }

            const invoiceTransactions = [
                ...transactions.filter(x => !x.IsTax && x.Id !== 0),
                ...newtransactions
            ]
            const invoice = await SettleAndPostInvoice({
                CurrencyID: state.paymentCurrency,
                HotelId: hotel && hotel.Id,
                SkipRecalcBalance: false,
                TemplateID: templateId,
                ReservationID: resEntity && resEntity.EntityId,
                FolioTransactions: invoiceTransactions,
                NewFolioTransactions: unsavedTransactions,
                InvoiceGuestDetails: {
                    IsChangeGuestDetails: !_.isEmpty(guestDetails),
                    GuestTitle: guestDetails.salutation,
                    GuestFirstName: guestDetails.fname || guestDetails.customerName,
                    GuestLastName: guestDetails.lname,
                    CustomerVATNumber: customerDetails.vatNumber || guestDetails.vat,
                    GuestStreetAndNumber: guestDetails.street,
                    GuestCity: guestDetails.city,
                    GuestZip: guestDetails.zip,
                    GuestCountryName: guestDetails.country,
                    GuestState: guestDetails.state,
                    GuestPhone: guestDetails.phone,
                    GuestEmail: guestDetails.email,
                    GuestIdentityCardNumber: guestDetails.idnumber,
                    Comment: guestDetails.comments
                }
            })
            newtransactions = [...(invoice.SettlementFolioTransactions || []), ...newtransactions]
            let createTemplate = true;

            if (hotel?.HotelFeatures?.filter(f => f.FeatureID == HotelFeatureCodes.TaxAuthority).length > 0 &&
                confirmationNumber)
                {
                const error = await Invoice.GetTaxAuthorityApproval({
                    Invoice_ID: invoice.Id.toString(),
                    Customer_VAT_Number: parseInt(folio?.Owner?.EntityTypeId == EntityType.Guest ? guestDetails.idnumber : guestDetails.vat || customerDetails.vatNumber) || 0, 
                    Customer_Name: folio?.Owner?.EntityTypeId == EntityType.Guest ? guestDetails.fname + " " + guestDetails.lname : customerDetails.name,
                    Invoice_Reference_Number: invoice.InvoiceNumber,
                    OriginalInvoiceIdAllocationRejected: selfInvoice?.OriginalInvoiceIdAllocationRejected || null
                })
                if (error !== '200') {
                    createTemplate = false;
                    onWarning(error)
                }
            }
            if (createTemplate) {
                const invoiceParameters = [
                    { key: "CurrencyId", value: state.paymentCurrency },
                    { key: "FolioId", value: folio && folio.Id },
                    { key: "IsGroupByProduct", value: hotel.HotelParameter.IsDisplayFolioTransactionsGroupedByProduct },
                    { key: "IsGroupByTransactionCodeAndType", value: true },
                    { key: "InvoiceId", value: invoice.Id },
                    { key: "InvoiceTitle", value: "Copy Invoice" },
                    { key: "GuestTitle", value: guestDetails.salutation || "" },
                    { key: "GuestFirstName", value: guestDetails.fname || guestDetails.customerName || folio.Owner?.Name || "" },
                    { key: "GuestLastName", value: guestDetails.lname || "" },
                    { key: "CustomerVATNumber", value: customerDetails.vatNumber || guestDetails.vat || "" },
                    { key: "GuestStreetAndNumber", value: guestDetails.street || "" },
                    { key: "GuestCity", value: guestDetails.city || "" },
                    { key: "GuestZip", value: guestDetails.zip || "" },
                    { key: "GuestCountryName", value: guestDetails.country || "" },
                    { key: "GuestState", value: guestDetails.state || "" },
                    { key: "GuestPhone", value: guestDetails.phone || "" },
                    { key: "GuestEmail", value: guestDetails.email || "" },
                    { key: "GuestIdentityCardNumber", value: guestDetails.idnumber || "" },
                    { key: "Comments", value: guestDetails.comments || "" }
                ]
                if(selfInvoice) {
                    invoiceParameters.push({ key: "SelfInvoiceNote", value: true })
                }

                const copy = await RenderTemplateWithReCalcTax({
                    HotelID: hotel && hotel.Id,
                    TemplateID: templateId,
                    IsPrintable: true,
                    FileName: "CopyTaxInvoice",
                    Paramters: [
                        ...invoiceParameters,
                        { key: "InvoiceTitle", value: "CopyTaxInvoice" }
                    ],
                    GroupBy: groupedBy?.toString()
                });

                const promises = [

                    RenderTemplateWithReCalcTax({
                        HotelID: hotel && hotel.Id,
                        TemplateID: templateId,
                        IsPrintable: true,
                        FileName: "OriginalTaxInvoice",
                        Paramters: [
                            ...invoiceParameters,
                            { key: "InvoiceTitle", value: "OriginalTaxInvoice" }
                        ],
                        GroupBy: groupedBy?.toString()
                    })
                ]
                if (state.printReceipt && newtransactions.length) {
                    const receiptParams = [
                        { key: "CurrencyId", value: state.paymentCurrency },
                        { key: "FolioId", value: folio && folio.Id },
                        { key: "FolioTransactionId", value: newtransactions[0].Id },
                        { key: "LanguageId", value: templateItem ? templateItem.LanguageId : 3 }
                    ]
                    promises.push(RenderTemplateToPdf({
                        EntityID: folio.Owner?.EntityId,
                        EntityTypeID: folio.Owner?.EntityTypeId,
                        FileName: "ReceiptOriginal",
                        Paramters: [...receiptParams, { key: "ReceiptTitle", value: "ReceiptOriginal" }],
                        TemplateTypeID: 17
                    }))
                    promises.push(RenderTemplateToPdf({
                        EntityID: folio.Owner?.EntityId,
                        EntityTypeID: folio.Owner?.EntityTypeId,
                        FileName: "ReceiptCopy",
                        Paramters: [...receiptParams, { key: "ReceiptTitle", value: "ReceiptCopy" }],
                        TemplateTypeID: 17
                    }))
                }
                const [original, receiptorig, receiptcopy] = await Promise.all(promises)

                const sendEmailPromises = []
                const prepareTemplate = (template, subject) => {
                    return {
                        ...template,
                        PdfDto: { ...original.PdfDto, DmzUrlPathAccessibleByBrowser: null },
                        IsAttachBodyAsPdf: true,
                        To: guestDetails.email,
                        Subject: text(subject)
                    }
                }
                const files = []
                if (original) {
                    files.push(original.PdfDto.DmzUrlPathAccessibleByBrowser)
                    if (state.sendEmail && guestDetails.email) {
                        sendEmailPromises.push(SendEmail(prepareTemplate(original, "OriginalTaxInvoice")))
                    }
                }
                if (copy) {
                    files.push(copy.PdfDto.DmzUrlPathAccessibleByBrowser)
                }
                if (receiptorig) {
                    files.push(receiptorig.DmzUrlPathAccessibleByBrowser)
                    if (state.sendEmail && guestDetails.email) {
                        sendEmailPromises.push(SendEmail(prepareTemplate(receiptorig, "ReceiptOriginal")))
                    }
                }
                if (receiptcopy) {
                    files.push(receiptcopy.DmzUrlPathAccessibleByBrowser)
                }
                if (slip) {
                    files.push(slip.PathOriginal)
                    files.push(slip.PathCopy)
                }

                try {
                    await Promise.all(sendEmailPromises)
                } catch (error) {
                    error.status === 400 ? onWarning(error) : onError(error)
                }

                files.forEach(file => {
                    saveAs(file)
                })
            }
            setGuestDetails({})
            setOpen(false)
            if (params && params.OnSave) {
                params.OnSave()
            }

        } catch (error) {
            error.status === 400 ? onWarning(error) : onError(error)
        }
        finally {
            setState({ ...state, ChecksDetails: [] })
            loader(false)
        }
        setConfirmationNumber(false)
    }

    const isZeroBalance = () => {
        return Math.abs(state.amount) <= 0.01
    }

    const disableIssueInvoice = useCallback(() => {

        // HS-69601: wait for the server balance so the payment amount matches the invoice
        if (serverBalanceLoading || serverBalanceError) {
            return true
        }

        if (hasNegativeBalance && (activeTab === 1 || !hasRefundAuthorization)) {
            return true
        }

        return (activeTab === 0 && !state.transactionCodeID) &&
            !isZeroBalance()
    }, [state, activeTab, serverBalanceLoading, serverBalanceError])

    const selectTemplate = useCallback(options => {
        if (options && options.length) {
            return options.find(x => x.item.LanguageId === folio.Owner?.LanguageID) || options[0]
        } else {
            return null
        }

    }, [folio])

    const taxAuthorityValidation = () => {
        const customerName = folio?.Owner?.EntityTypeId == EntityType.Guest ?
            guestDetails.fname + " " + guestDetails.lname :
            customerDetails.name

        if (confirmationNumber && customerName.length > maxCustomerNameLength)
            return onWarning(message('CustomerNameIsTooLong'))

         const vatNumber = guestDetails.vat || customerDetails.vatNumber     
         const INT_MAX = 2147483647;    
        if (confirmationNumber &&
            folio?.Owner?.EntityTypeId != EntityType.Guest &&
            (!vatNumber || isNaN(vatNumber) || parseInt(vatNumber) > INT_MAX))
            return onWarning(message('VatNumberLengthValidation'))

        if (confirmationNumber &&
            folio?.Owner?.EntityTypeId == EntityType.Guest &&
            !guestDetails.idnumber) {
            setConfirmPopupStatus(true)
        }
        else {
            save()
        }
    }

    const Is3dsOpenChange = (is3dsOpen) => {
        setDisplaySave(!is3dsOpen);
    };

    return <React.Fragment>
        <Modal size="fullscreen" onClose={onCancel} open={open} {...rest}>
            <Modal.Header>{text("Proforma")}</Modal.Header>
            <Modal.Content style={{ display: "flex", flexDirection: "column" }}>
                <Form as={Segment}>
                    <Form.Group inline style={{ marginBottom: 0 }}>
                        <Form.Field label={text("Template")} control={TemplatesDropDown} spec={templatesSpec} descriptionField="" selectFirst={selectTemplate} onChange={setTemplateId} onChangeItem={setTemplateItem} />
                        <InvoiceDetailsPopup trigger={
                            <Button>{text("InvoiceDetails")}</Button>
                        } state={guestDetails} setState={setGuestDetails} />
                        <Form.Field label={text("InvoiceCurrency")} control={CurrencyDropDown} value={state.paymentCurrency} textField="Name" descriptionField="Symbol" onChange={val => setState({
                            ...state,
                            paymentCurrency: val
                        })}></Form.Field>
                        <Form.Checkbox label={text("PrintReceipt")} onChange={(e, { checked }) => setState({
                            ...state,
                            printReceipt: checked
                        })}></Form.Checkbox>
                        <Form.Checkbox label={text("SendEmail")} checked={state.sendEmail} onChange={(e, { checked }) => setState({
                            ...state,
                            sendEmail: checked
                        })}></Form.Checkbox>
                        <Form.Input disabled={!state.sendEmail} style={{ minWidth: 300 }} type="email" name="email" label="Email" value={guestDetails.email} onChange={(e, { value }) => {
                            setGuestDetails({
                                ...guestDetails,
                                email: value
                            })
                        }}></Form.Input>
                        <Form.Field label={text("GroupBy")} control={DropDown} data={groupedBySelection} value={groupedBy} onChange={setGroupedBy} key='groupBy' valueField="key" textField="text" multiple style={{ minWidth: 150 }} />
                        {hotel?.HotelFeatures?.filter(f => f.FeatureID == HotelFeatureCodes.TaxAuthority).length > 0 &&
                            <Form.Checkbox label={text("ConfirmationNumber")} onChange={(e, { checked }) => setConfirmationNumber(checked)}
                                disabled={confirmationNumberDisabled} checked={confirmationNumber}></Form.Checkbox>}
                        <div style={{ flex: 1 }} />
                        {hasRefundAuthorization && <Form.Checkbox checked={refund} label={text("Refund")} onChange={(e, { checked }) => setRefund(checked)}></Form.Checkbox>}
                    </Form.Group>
                </Form>
                <Grid style={{ flex: 1 }}>
                    <Grid.Row style={{ margin: 0 }}>
                        <Grid.Column width="10">
                            <Segment style={{ whiteSpace: "initial", overflow: "auto", position: "absolute", marginLeft: 14, top: 0, bottom: 0, left: 0, right: 0 }}>
                                {template.Body ? convertStatic(template.Body) : <Dimmer inverted active><Loader size="large" /></Dimmer>}
                            </Segment>
                        </Grid.Column>
                        <Grid.Column width="6">
                            <Dimmer.Dimmable dimmed={isZeroBalance()} style={{ height: "100%" }}>
                                <Dimmer inverted active={isZeroBalance() || !UserHasPermission(PermissionsActions.AccessToCharge)} />
                                <PaymentTabs
                                    state={state}
                                    setState={setState}
                                    dialogRef={dialogRef}
                                    hotel={hotel}
                                    setActiveTab={setActiveTab}
                                    folio={folio}
                                    readOnlyAmount={true}
                                    onIs3dsOpenChange={Is3dsOpenChange}
                                    isPaymentDialogReadOnly= { hasNegativeBalance} />
                            </Dimmer.Dimmable>
                        </Grid.Column>
                    </Grid.Row>
                </Grid>
            </Modal.Content>
            <Modal.Actions>
                <Button negative size="small" onClick={() => {
                    setOpen(false)
                    if (onCancel) {
                        onCancel()
                    }
                }}>{text("Cancel")}</Button>
                <Button primary size="small" onClick={print}>{text("PrintStatement")}</Button>
                <Button primary disabled={!guestDetails.email || !state.sendEmail} size="small" onClick={send}>{text("SendStatementToEmail")}</Button>
                <Button positive disabled={disableIssueInvoice()} size="small" onClick={taxAuthorityValidation}>{text("IssueInvoice")}</Button>
            </Modal.Actions>
        </Modal>
        { displaySave && <Confirm
            open={confirmPopupStatus}
            header="Default Guest Identity"
            content={message('DefualtGuestIdentity')}
            confirmButton={{ content: "Yes", positive: true }}
            cancelButton={{ content: "No", negative: true }}
            onCancel={() => setConfirmPopupStatus(false)}
            onConfirm={() => { setConfirmPopupStatus(false); save() }}
            size={"tiny"}
        />}

    </React.Fragment>
}

ProformaPopup.propTypes = {
    onCancel: PropTypes.func,
    hotel: PropTypes.object,
    folio: PropTypes.object,
    transactionIds: PropTypes.array,
    controller: PropTypes.any,
    open: PropTypes.bool,
    currencyId: PropTypes.number
}

export default ProformaPopup
